/*
 * <community-social>: gentle scroll parallax for the three photos of the community section.
 * While the section is on screen, a rAF-throttled scroll handler maps its position in the viewport to
 * --parallax-y (px, -40..40) and --rotate-factor (1 at the edges, easing to 0.75 when centered) on the host.
 * The CSS applies them per photo with its own rate. Nothing runs under reduced motion.
 */
if (!customElements.get('community-social')) {
  const MAX_SHIFT = 40;
  const EDGE_ROTATION = 1;
  const CENTER_ROTATION = 0.75;

  class CommunitySocial extends HTMLElement {
    connectedCallback() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.frame = 0;
      this.visible = false;
      this.onScroll = this.requestUpdate.bind(this);
      this.onMotionChange = this.sync.bind(this);

      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
          this.sync();
        },
        { rootMargin: '25% 0px' }
      );
      this.observer.observe(this);
      this.reducedMotion.addEventListener('change', this.onMotionChange);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.reducedMotion?.removeEventListener('change', this.onMotionChange);
      this.stopListening();
    }

    sync() {
      if (this.visible && !this.reducedMotion.matches) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.requestUpdate();
      } else {
        this.stopListening();
        this.style.removeProperty('--parallax-y');
        this.style.removeProperty('--rotate-factor');
      }
    }

    stopListening() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight;
      // -1 when the section's center is a half viewport (plus half its height) above the viewport center, +1 below.
      const progress = (rect.top + rect.height / 2 - viewport / 2) / (viewport / 2 + rect.height / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      const rotateFactor = CENTER_ROTATION + (EDGE_ROTATION - CENTER_ROTATION) * Math.abs(clamped);

      this.style.setProperty('--parallax-y', `${(clamped * MAX_SHIFT).toFixed(2)}px`);
      this.style.setProperty('--rotate-factor', rotateFactor.toFixed(3));
    }
  }

  customElements.define('community-social', CommunitySocial);
}
